import { OptionsApi } from '@core/swagger-client';
import { PollWithVotes } from '@core/types';
import { Button, CompositePageHeader, ContainerNoTopSpace, ErrorMessagePage, Heading, LoaderPage, PageHeader, PageHeaderContent, Select } from '@core/ui';
import { getRouteApi } from '@online-webinar/router';
import { BackToOriginLink } from '@online-webinar/ui-frontend';
import { useQuery } from '@tanstack/react-query';
import { cloneDeep } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PollBasicInfo, PollCard } from '../../molecules';

enum Lifecyle {
    Combined,
    Live,
    OnDemand
}

const route = getRouteApi('/$webinarId/poll-result');
export const WebinarPollResults = () => {
    const { webinarId } = route.useParams();
    const { t } = useTranslation();

    const [pollResults, setPollResults] = useState<PollWithVotes[]>([]);
    const [lifecycle, setLifecycle] = useState<Lifecyle>(Lifecyle.Combined);

    const { data: livePollResults, isLoading: isLoadingLive, error: pollError } = useQuery(OptionsApi.insights.getLivePollResults(webinarId));

    const {
        data: onDemandPollResults,
        isLoading: isLoadingOnDemand,
        error: onDemandPollError
    } = useQuery(OptionsApi.insights.getOnDemandPollResults(webinarId));

    const { data: webinar, isLoading: isLoadingWebinar, error: webinarError } = useQuery(OptionsApi.webinar.get(webinarId));

    useEffect(() => {
        if (!livePollResults || !onDemandPollResults) {
            return;
        }

        let polls: PollWithVotes[] = [];
        if (lifecycle === Lifecyle.Live) {
            polls = livePollResults;
        } else if (lifecycle === Lifecyle.OnDemand) {
            polls = onDemandPollResults;
        } else {
            for (let poll of livePollResults) {
                polls.push(cloneDeep(poll));
            }
            for (let poll of onDemandPollResults) {
                const existing = polls.find((p) => p.pollId === poll.pollId);
                if (existing) {
                    for (let option of poll.options) {
                        const existingOption = existing.options.find((o) => o.optionId === option.optionId);
                        if (existingOption) {
                            existingOption.participants.push(...option.participants);
                            existingOption.votes += option.votes;
                            existingOption.weightedVotes += option.weightedVotes;
                        } else {
                            existing.options.push(cloneDeep(option));
                        }
                    }
                    existing.totalVotes += poll.totalVotes;
                    existing.totalWeightedVotes += poll.totalWeightedVotes;
                } else {
                    polls.push(cloneDeep(poll));
                }
            }
        }
        setPollResults(polls);
    }, [livePollResults, onDemandPollResults, lifecycle]);

    const error = pollError ?? onDemandPollError ?? webinarError;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (isLoadingLive || isLoadingWebinar || isLoadingOnDemand || !webinar) {
        return <LoaderPage />;
    }

    const exportExcelAsync = async () => {
        const xlsx = await import('xlsx');
        const workbook = xlsx.utils.book_new();
        workbook.Props = {
            Application: 'OnlineWebinar.com',
            AppVersion: '1.0.0',
            Author: 'OnlineWebinar.com',
            CreatedDate: new Date(),
            Title: 'Poll results for ' + webinar.title
        };

        const summaryData: (string | number)[][] = [];
        pollResults.forEach((poll, pollIndex) => {
            summaryData.push([pollIndex + 1, poll.question]);
            poll.options.forEach((option) => {
                summaryData.push(['', option.text, option.votes, option.weightedVotes]);
            });
            summaryData.push([]);
        });

        // prettier-ignore
        const summary = xlsx.utils.aoa_to_sheet([
            ['Poll results for ' + webinar.title],
            [],
            ['Exported on', , , , new Date()],
            [],
            ['Number of polls asked:', , , , pollResults.length],
            [],
            ...summaryData
        ]);
        xlsx.utils.book_append_sheet(workbook, summary, 'Summary');

        pollResults.forEach((question, questionIndex) => {
            // prettier-ignore
            const data: (number | string | undefined)[][] = [
                [`Details for ${questionIndex + 1}. ${question.question}`],
                [],
                ['First name', 'Last name', 'Nickname', 'Email address', 'Company', 'Country', 'Answer', 'Weight', 'Points'],
            ];

            question.options.forEach((option) => {
                option.participants.forEach((vote) => {
                    data.push([
                        vote.participant.firstName || '',
                        vote.participant.lastName || '',
                        vote.participant.nickname || '',
                        vote.participant.emailAddress || '',
                        vote.participant.companyName || '',
                        vote.participant.countryCode || '',
                        option.text,
                        vote.weightedValue,
                        option.points
                    ]);
                });
            });

            const sheet = xlsx.utils.aoa_to_sheet(data);
            xlsx.utils.book_append_sheet(workbook, sheet, 'Question ' + (questionIndex + 1));
        });

        xlsx.writeFileXLSX(workbook, 'PollResults.xlsx');
    };

    const exportExcel = () => {
        exportExcelAsync().then(() => {
            // Done...
        });
    };

    return (
        <ContainerNoTopSpace>
            <PageHeader>
                <PageHeaderContent>{webinar && <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />}</PageHeaderContent>
            </PageHeader>

            <div className="px:4 md:px-6 pt-4 md:pt-10 max-w-4xl">
                <BackToOriginLink fallback="insights" />
                <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                    {t('Insights.Polls.PageHeading')}
                </Heading>

                <Select
                    value={lifecycle}
                    onChange={(e) => setLifecycle(parseInt(e.target.value))}
                    label={t('Insights.Polls.Select.Label')}
                    classNames="max-w-sm mb-10"
                >
                    <option value={Lifecyle.Combined}>{t('Insights.Polls.Select.Combined')}</option>
                    <option value={Lifecyle.Live}>{t('Insights.Polls.Select.Live')}</option>
                    <option value={Lifecyle.OnDemand}>{t('Insights.Polls.Select.OnDemand')}</option>
                </Select>

                <div className="flex justify-between flex-wrap gap-4 mb-8">
                    <PollBasicInfo pollCount={pollResults.length}></PollBasicInfo>
                    <Button onClick={exportExcel} variant="primary">
                        {t('Insights.Polls.Button.Export')}
                    </Button>
                </div>
                {pollResults.map((poll, index) => (
                    <PollCard poll={poll} index={index}></PollCard>
                ))}
            </div>
        </ContainerNoTopSpace>
    );
};
